import { useState } from 'react';
import { Grid, Typography, Divider, Button, Box } from '@mui/material';
import { OpenInNewOutlined, CheckCircle, Cancel, ReplayCircleFilled } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/shared/functions/formatDate';
import { useSelector } from 'react-redux';
import { RootState } from 'src/shared/services/store';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { FAILED_STATUSES, PaymentsStatusEnum } from 'src/shared/models/enums/paymentsStatus.enum';
import { InvoicePayment } from '../models/invoicePayment';
import { Invoice } from '../models/invoice';
import { PaymentDetail } from './paymentDetail';

type InvoiceMissionDetailsProps = {
  balance: number;
  invoice: Invoice;
  refetchData?: () => void;
};

// Uppdragstyper som är medlemsavgifter och kan ha flyttat fram medlemmens expireDate vid betalning → varna vid radering.
const MEMBERSHIP_MISSION_TYPES = ['member', 'autogiro', 'local', 'card', 'card2', 'einvoice'];

const InvoiceMissionDetails = (props: InvoiceMissionDetailsProps) => {
  const { balance, invoice, refetchData } = props;
  const { mission, payments } = invoice;

  const { t } = useTranslation(['invoice', 'common']);
  const user = useSelector((state: RootState) => state.oidc.user);

  // Klick på en betalningsrad öppnar detalj-modalen (PaymentDetail), där ev. radera-knapp ligger.
  const [selectedPayment, setSelectedPayment] = useState<InvoicePayment | null>(null);

  const FSPayLink = `${process.env.REACT_APP_FS_PAY_URL}/${invoice.invoice_id}/${invoice.invoiceGuid}/${user?.profile.custname}`;

  const isMemberFeeInvoice = !!mission?.type && MEMBERSHIP_MISSION_TYPES.includes(mission.type);

  return (
    <ContentSection header={t('invoice:payment.paymentInformation')}>
      {mission ? (
        <>
          <Grid container item xs={12}>
            <Grid xs={12} item>
              <Typography sx={{ fontWeight: 600, fontSize: 16 }} variant='h6'>
                {t('invoice:payment.payee')}:
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Typography sx={{ wordBreak: 'break-word' }}>{mission.senderName}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid xs={12} item>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 2 }} variant='h6'>
                {t('invoice:payment.paymentAccount')}:
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Typography>{mission.accountNr}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid xs={12} item>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 2 }} variant='h6'>
                {t('invoice:payment.balance')}:
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Typography>
                <AmountWithCurrency amount={balance} />
              </Typography>
            </Grid>
          </Grid>
          {invoice.invoiceGuid && user && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<OpenInNewOutlined />}
                href={FSPayLink}
                target='_blank'
                rel='noopener noreferrer'
                fullWidth
              >
                {t('invoice:payment.paymentLink')}
              </Button>
            </Grid>
          )}
          {payments && payments?.length > 0 && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
            </Grid>
          )}
          <Grid item xs={12} flexDirection='column'>
            {payments &&
              payments.map((payment: InvoicePayment) => {
                const isRenewed = payment.status === PaymentsStatusEnum.AgWillRetry;
                const isFailed = payment.status != null && FAILED_STATUSES.includes(payment.status as PaymentsStatusEnum) && !isRenewed;
                const isSwish = payment.status === PaymentsStatusEnum.SwishFailed;
                return (
                  <Box
                    key={payment.invoicePayment_id}
                    // Betalningsraden saknar ofta egen OCR (sätts inte vid registrering) – fakturans OCR gäller ändå.
                    onClick={() => setSelectedPayment({ ...payment, ocr: payment.ocr || invoice.ocr })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1.5,
                      px: 0.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    {(() => {
                      if (isRenewed) return <ReplayCircleFilled sx={{ color: '#ff9800', fontSize: 20, flexShrink: 0 }} />;
                      if (isFailed) return <Cancel sx={{ color: '#f44336', fontSize: 20, flexShrink: 0 }} />;
                      return <CheckCircle sx={{ color: '#00af83', fontSize: 20, flexShrink: 0 }} />;
                    })()}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box display='flex' alignItems='center' justifyContent='space-between'>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {(() => {
                            if (isRenewed) return t('invoice:payment.renewedCoverage');
                            if (isFailed) return t('invoice:payment.failedPayment');
                            return t('invoice:payment.paymentRegistratedThrough');
                          })()}{' '}
                          {payment.source}
                          {(isFailed || isRenewed) && !isSwish && ' (autogiro)'}
                          {!isFailed && !isRenewed && (
                            <>
                              , <AmountWithCurrency amount={payment.amount} />
                            </>
                          )}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                          {formatDate(payment.date)}
                        </Typography>
                      </Box>
                      {(isFailed || isRenewed) && payment.statusMessage && (
                        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.2 }}>
                          {payment.statusMessage}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
          </Grid>
        </>
      ) : (
        <Typography>{t('invoice:payment.noPaymentInfo')}</Typography>
      )}
      {selectedPayment && (
        <PaymentDetail
          payment={selectedPayment}
          setOpenModal={(open) => {
            if (!open) setSelectedPayment(null);
          }}
          onDeleted={refetchData}
          isMemberFeeInvoice={isMemberFeeInvoice}
        />
      )}
    </ContentSection>
  );
};

export default InvoiceMissionDetails;
